import React, { useState } from "react";
import {
  InformationCircleIcon,
  MinusIcon,
  XMarkIcon,
  ClipboardDocumentListIcon,
  ClipboardDocumentIcon,
} from "@heroicons/react/24/outline";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import {
  ComponentEnum,
  ComponentI,
  isSupportedComponentType,
} from "../../../types";
import { useNotification } from "../../../hooks/useNotification";
import Button from "../../../componentLibrary/Button";
import Tooltip from "../../../componentLibrary/Tooltip";
import Card from "../../../componentLibrary/Card";
import Input from "../../../componentLibrary/Input";
import { SelectedComponentType } from "../utils/selectedComponentUtils";
import { onCopyClipboard } from "../utils/onCopyClipboard";
import { handleCopyComponent } from "../utils/selectedComponentUtils";
import { DISPLAY_VARIABLES_MINIMIZED } from "../utils/componentGroup";
import ImageContentCard from "./contentCardComponents/ImageContentCard";
import InputContentCard from "./contentCardComponents/InputContentCard";
import LogicComponentContentCard from "./contentCardComponents/LogicComponentContentCard";
import MultipleChoiceContentCard from "./contentCardComponents/MultipleChoiceContentCard";
import RatingScaleContentCard from "./contentCardComponents/RatingScaleContentCard";
import TextContentCard from "./contentCardComponents/TextContentCard";
import ToggleTextContentCard from "./contentCardComponents/ToggleTextContentCard";
import VideoContentCard from "./contentCardComponents/VideoContentCard";
import LessonLinkContentCard from "./contentCardComponents/LessonLinkContentCard";
import UserListContentCard from "./contentCardComponents/UserListContentCard";
import CustomLinkContentCard from "./contentCardComponents/CustomLinkContentCard";
import ActionDetailContentCard from "./contentCardComponents/ActionDetailContentCard";
import PriorityListContentCard from "./contentCardComponents/PriorityListContentCard";
import DateTimeContentCard from "./contentCardComponents/DateTimeContentCard";
import CalloutBoxContentCard from "./contentCardComponents/CalloutBoxContentCard";
import ForEachContentCard from "./contentCardComponents/ForEachContentCard";
import ToggleContentCard from "./contentCardComponents/ToggleContentCard";
import AddToCalendarContentCard from "./contentCardComponents/AddToCalendarContentCard";

interface GroupedComponentsProps {
  groupTitle: string;
  groups: ComponentEnum[];
  component: ComponentI;
  handleContentChange: (
    id: string,
    field: string,
    value: any, //eslint-disable-line @typescript-eslint/no-explicit-any
  ) => void;
  onRemove: () => void;
  provided: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  selectedComponent: SelectedComponentType;
  handleComponentClick: (selectedComponent: SelectedComponentType) => void;
  variableUptoCurrentPage: string[];
  componentRefs: React.RefObject<{ [key: string]: HTMLDivElement | null }>;
  handleTitleClick?: () => void;
  handleTypeChange: (componentId: string, value: ComponentEnum) => void;
  isMinimized: boolean;
  setIsMinimized: (isMinimized: boolean) => void;
}

export const GroupedComponents: React.FC<GroupedComponentsProps> = ({
  groups,
  component,
  handleContentChange,
  onRemove,
  provided,
  selectedComponent,
  handleComponentClick,
  variableUptoCurrentPage,
  componentRefs,
  handleTitleClick,
  handleTypeChange,
  isMinimized,
  setIsMinimized,
}) => {
  const [notes, setNotes] = useState<string>("");
  const { notify } = useNotification();

  const copyFunction = () => {
    if (component?.content.variable) {
      onCopyClipboard(`{{${component.content.variable}}}`);
      notify("success", "Variable Copied to Clipboard!");
    } else {
      notify("error", "No Variable to copy");
    }
  };

  const returnComponent = (currentComponentType: ComponentEnum) => {
    switch (currentComponentType) {
      case ComponentEnum.VIDEO:
        return (
          <VideoContentCard
            component={component}
            handleContentChange={handleContentChange}
          />
        );
      case ComponentEnum.TEXT:
        return (
          <TextContentCard
            component={component}
            handleContentChange={handleContentChange}
          />
        );
      case ComponentEnum.INPUT:
        return (
          <InputContentCard
            component={component}
            handleContentChange={handleContentChange}
          />
        );
      case ComponentEnum.IMAGE:
        return (
          <ImageContentCard
            component={component}
            handleContentChange={handleContentChange}
            provided={provided}
          />
        );
      case ComponentEnum.MULTIPLE_CHOICE:
        return (
          <MultipleChoiceContentCard
            component={component}
            handleContentChange={handleContentChange}
            variableNames={variableUptoCurrentPage}
          />
        );
      case ComponentEnum.RATING_SCALE:
        return (
          <RatingScaleContentCard
            component={component}
            handleContentChange={handleContentChange}
          />
        );
      case ComponentEnum.TOGGLE_TEXT:
        return (
          <ToggleTextContentCard
            component={component}
            handleContentChange={handleContentChange}
          />
        );
      case ComponentEnum.LOGIC_COMPONENT:
        return (
          <LogicComponentContentCard
            component={component}
            handleContentChange={handleContentChange}
            variableUptoCurrentPage={variableUptoCurrentPage}
            provided={provided}
            selectedComponent={selectedComponent}
            handleComponentClick={handleComponentClick}
            componentRefs={componentRefs}
          />
        );
      case ComponentEnum.LESSON_LINK:
        return (
          <LessonLinkContentCard
            component={component}
            handleContentChange={handleContentChange}
            provided={provided}
          />
        );
      case ComponentEnum.USER_LIST:
        return (
          <UserListContentCard
            component={component}
            handleContentChange={handleContentChange}
          />
        );
      case ComponentEnum.CUSTOM_LINK:
        return (
          <CustomLinkContentCard
            component={component}
            handleContentChange={handleContentChange}
            provided={provided}
          />
        );
      case ComponentEnum.ACTION_DETAIL:
        return (
          <ActionDetailContentCard
            component={component}
            handleContentChange={handleContentChange}
            variableNames={variableUptoCurrentPage}
            provided={provided}
          />
        );
      case ComponentEnum.PRIORITY_LIST:
        return (
          <PriorityListContentCard
            component={component}
            handleContentChange={handleContentChange}
            variableNames={variableUptoCurrentPage}
          />
        );
      case ComponentEnum.DATE_TIME:
        return (
          <DateTimeContentCard
            component={component}
            handleContentChange={handleContentChange}
            provided={provided}
          />
        );
      case ComponentEnum.CALLOUT_BOX:
        return (
          <CalloutBoxContentCard
            component={component}
            handleContentChange={handleContentChange}
            provided={provided}
          />
        );
      case ComponentEnum.FOR_EACH:
        return (
          <ForEachContentCard
            component={component}
            handleContentChange={handleContentChange}
            variableUptoCurrentPage={variableUptoCurrentPage}
            provided={provided}
            selectedComponent={selectedComponent}
            handleComponentClick={handleComponentClick}
            componentRefs={componentRefs}
          />
        );
      case ComponentEnum.TOGGLE:
        return (
          <ToggleContentCard
            component={component}
            handleContentChange={handleContentChange}
            variableUptoCurrentPage={variableUptoCurrentPage}
            provided={provided}
            selectedComponent={selectedComponent}
            handleComponentClick={handleComponentClick}
            componentRefs={componentRefs}
          />
        );
      case ComponentEnum.ADD_TO_CALENDAR:
        return (
          <AddToCalendarContentCard
            component={component}
            handleContentChange={handleContentChange}
            provided={provided}
          />
        );
      default: {
        // DB may contain component types from other envs (e.g. stage-only); ignore unsupported types
        if (!isSupportedComponentType(currentComponentType)) {
          return null;
        }
        const _exhaustiveCheck: never = currentComponentType;
        throw new Error(`Unhandled component type: ${_exhaustiveCheck}`);
      }
    }
  };

  const renderMinimizedDetails = () => {
    const renderTextComponent = () => {
      return (
        <div
          className="ql-editor max-h-[75px] overflow-y-auto"
          dangerouslySetInnerHTML={{
            __html: component.content.text,
          }}
        />
      );
    };

    return (
      <div className="mt-2">
        {component.type === ComponentEnum.TEXT && renderTextComponent()}
        {(DISPLAY_VARIABLES_MINIMIZED[component.type] ?? [])
          .filter((variable) => component.content[variable])
          .map((variable) => (
            <div key={variable}>
              <div className="text-md font-semibold truncate">
                {variable}: {component.content[variable]}
              </div>
            </div>
          ))}
        {notes && (
          <div className="text-md font-semibold truncate">Notes: {notes}</div>
        )}
      </div>
    );
  };

  return (
    <div
      className="p-2 rounded-lg bg-gray-300"
      onClick={() => handleTitleClick?.()}
    >
      <div className="flex flex-row justify-between items-center">
        <div className="flex flex-row gap-2 items-center">
          <h3 className="hover:cursor-pointer">{component.type}</h3>
          <Tooltip
            text={
              "Click the title to select this component. Click again to deselect. Components you add or paste will appear below the selected component."
            }
            position="right"
            isActive={true}
            icon={<InformationCircleIcon className="h-4 w-4 text-gray-600" />}
          />
        </div>
        <div
          className="flex flex-row gap-2 items-center"
          onClick={(e) => e.stopPropagation()}
        >
          {component.content.variable && (
            <Button.Square
              variant="alternative"
              onClick={copyFunction}
              title="Copy Variable"
            >
              <ClipboardDocumentListIcon className="h-6 w-6" />
            </Button.Square>
          )}
          <Button.Square
            variant="alternative"
            onClick={() =>
              handleCopyComponent(
                {
                  component: component,
                  parentComponent: null,
                },
                notify,
              )
            }
            title="Copy Component"
          >
            <ClipboardDocumentIcon className="h-6 w-6" />
          </Button.Square>
          <Button
            variant="alternative"
            size="sm"
            onClick={() => setIsMinimized(!isMinimized)}
            title={isMinimized ? "Maximize" : "Minimize"}
          >
            {isMinimized ? (
              <CropSquareIcon className="h-5 w-5" />
            ) : (
              <MinusIcon className="h-5 w-5" />
            )}
          </Button>
          <Button
            variant="alternative"
            size="sm"
            onClick={onRemove}
            title="Remove"
          >
            <XMarkIcon className="h-5 w-5" />
          </Button>
        </div>
      </div>
      {!isMinimized ? (
        <div className="mt-2">
          <div className="flex flex-row gap-2 mb-4 flex-wrap">
            {groups.map((group) => (
              <Button
                key={group}
                variant={group === component.type ? "primary" : "secondary"}
                className={`flex flex-row gap-2 ${group === component.type ? "ring-2 ring-offset-2 ring-electrum-300 ring-offset-gray-300" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  group !== component.type &&
                    handleTypeChange(component._id, group);
                }}
                size="sm"
              >
                {group}
              </Button>
            ))}
          </div>
          <div onClick={(e) => e.stopPropagation()}>
            {returnComponent(component.type)}
          </div>
          <Card className="mt-2">
            <Input
              placeholder="Any additional note"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Card>
        </div>
      ) : (
        <>{renderMinimizedDetails()}</>
      )}
    </div>
  );
};
